import { supabase } from '../../../core/lib/supabase';

export interface AIUsageTokens {
  input: number;
  output: number;
  total: number;
}

export interface UsageByMessage {
  messageId: string;
  content: string;
  model: string;
  display_model_name?: string;
  cost: number;
  date: string;
  usageCount: number;
  tokens: AIUsageTokens;
}

export interface UsageBreakdown {
  name: string;
  executions_count: number;
  credits_used: number;
  percentage: number;
}

export interface AIUsageStats {
  totalSpend: number;
  limit: number | null;
  currency: string;
  totalTokens: AIUsageTokens;
  usageByMessage: UsageByMessage[];
  subscription_balance?: number;
  overage_limit?: number;
  current_overage_spend?: number;
  has_payment_method?: boolean;
  subscription_status?: string;
  plan_name?: string;
  usageByWorkPill: UsageBreakdown[];
  usageByProject: UsageBreakdown[];
}

/**
 * Obtiene la suscripción y el plan del usuario
 */
export async function fetchUserSubscription(userId: string) {
  const { data, error } = await supabase
    .from('subscriptions')
    .select(`
      subscription_status,
      has_payment_method,
      current_period_start,
      plans!subscriptions_plan_id_fkey (
        name,
        monthly_limit,
        currency,
        price_monthly
      )
    `)
    .eq('user_id', userId)
    .single();

  if (error) throw error;
  return data;
}

/**
 * Obtiene el desglose de uso por WorkPill
 */
export async function fetchUsageByWorkPill(userId: string) {
  const { data, error } = await supabase
    .from('ai_usage')
    .select(`
      total_spend,
      messages!inner (
        conversations!inner (
          workpill_executions!inner (
            workpills!inner (
              name
            )
          )
        )
      )
    `)
    .eq('user_id', userId);

  if (error) throw error;

  const groups: Record<string, UsageBreakdown> = {};
  data.forEach((row: any) => {
    const name = row.messages?.conversations?.workpill_executions?.workpills?.name || 'Unknown';
    if (!groups[name]) groups[name] = { name, executions_count: 0, credits_used: 0, percentage: 0 };
    groups[name].executions_count += 1;
    groups[name].credits_used += row.total_spend || 0;
  });

  const totalCredits = Object.values(groups).reduce((sum, item) => sum + item.credits_used, 0);
  return Object.values(groups).map(item => ({
    ...item,
    percentage: totalCredits > 0 ? Math.round((item.credits_used / totalCredits) * 100) : 0
  }));
}

/**
 * Obtiene el desglose de uso por Proyecto (WorkPill Execution)
 */
export async function fetchUsageByProject(userId: string) {
  const { data, error } = await supabase
    .from('ai_usage')
    .select(`
      total_spend,
      messages!inner (
        conversations!inner (
          workpill_executions!inner (
            name
          )
        )
      )
    `)
    .eq('user_id', userId);

  if (error) throw error;

  const groups: Record<string, UsageBreakdown> = {};
  data.forEach((row: any) => {
    const name = row.messages?.conversations?.workpill_executions?.name || 'Unnamed Project';
    if (!groups[name]) groups[name] = { name, executions_count: 0, credits_used: 0, percentage: 0 };
    groups[name].executions_count += 1;
    groups[name].credits_used += row.total_spend || 0;
  });

  const totalCredits = Object.values(groups).reduce((sum, item) => sum + item.credits_used, 0);
  return Object.values(groups).map(item => ({
    ...item,
    percentage: totalCredits > 0 ? Math.round((item.credits_used / totalCredits) * 100) : 0
  }));
}

/**
 * Obtiene el historial de uso detallado
 */
export async function fetchUsageHistory(userId: string, limit: number = 20) {
  const { data, error } = await supabase
    .from('ai_usage')
    .select(`
      id,
      created_at,
      total_spend,
      message_id,
      ai_pricing (
        display_model_name,
        model_name
      ),
      messages (
        conversations (
          workpill_executions (
            name
          )
        )
      )
    `)
    .eq('user_id', userId)
    .order('created_at', { ascending: false })
    .limit(limit);

  if (error) throw error;

  return (data || []).map((row: any) => ({
    messageId: row.message_id || row.id.toString(),
    cost: row.total_spend || 0,
    date: row.created_at,
    display_model_name: row.ai_pricing?.display_model_name || row.ai_pricing?.model_name || 'AI Assistant',
    model: row.ai_pricing?.model_name || 'N/A',
    project_name: row.messages?.conversations?.workpill_executions?.name || '-'
  }));
}

/**
 * Obtiene las estadísticas de uso de AI (Versión sin RPC)
 */
export async function fetchAIUsageStats(): Promise<AIUsageStats> {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('User not authenticated');

    const [sub, wallet, wpUsage, projUsage, history] = await Promise.all([
      fetchUserSubscription(user.id),
      supabase.from('user_wallets').select('*').eq('user_id', user.id).single(),
      fetchUsageByWorkPill(user.id),
      fetchUsageByProject(user.id),
      fetchUsageHistory(user.id)
    ]);

    const walletData = wallet.data;
    const plan: any = (sub as any)?.plans;

    return {
      totalSpend: wpUsage.reduce((sum, item) => sum + item.credits_used, 0),
      limit: plan?.monthly_limit || 0,
      currency: plan?.currency || 'USD',
      totalTokens: { input: 0, output: 0, total: 0 },
      usageByMessage: history as any,
      subscription_balance: walletData?.subscription_balance,
      overage_limit: walletData?.overage_limit,
      current_overage_spend: walletData?.current_overage_spend,
      has_payment_method: (sub as any)?.has_payment_method,
      subscription_status: (sub as any)?.subscription_status,
      plan_name: plan?.name,
      usageByWorkPill: wpUsage,
      usageByProject: projUsage
    };
  } catch (error) {
    console.error('💥 Error in fetchAIUsageStats:', error);
    throw error;
  }
}

/**
 * Actualiza el límite de gasto extra (overage)
 */
export async function updateOverageLimit(limit: number): Promise<void> {
  const { error } = await supabase.rpc('update_overage_limit', { p_limit: limit });
  if (error) throw error;
}

/**
 * Crea una sesión de configuración de pago
 */
export async function createSetupCheckout(returnUrl: string): Promise<string> {
  const { data, error } = await supabase.rpc('create_setup_checkout', { p_return_url: returnUrl });
  if (error) throw error;
  return data;
}
